import {
    buildYoutubeEmbedUrl,
    FullWidthVideoBlockData,
    GalleryBlockData,
    ImageBlockData,
    SpeakerBlockData,
    VideoWidth,
} from '@longy/common/cms'
import { Play } from 'lucide-react'
import { useState } from 'react'

import { cn } from '../../lib/utils'
import { useCmsTranslations, useRendererContext } from '../context/RendererContext'
import { CmsImage, CmsPicture } from '../primitives/CmsImage'
import { CmsLightbox } from '../primitives/CmsLightbox'
import { BlockSection, BlockTitle, byOrder } from './shared'

export const ImageRenderer = ({ media, mediaMobile, caption }: ImageBlockData) => (
    <BlockSection className="flex flex-col gap-2">
        <CmsPicture
            desktop={media}
            mobile={mediaMobile}
            sizes="(min-width: 1024px) 72rem, 100vw"
            className="h-auto w-full rounded-lg object-cover"
        />
        {caption && <p className="text-muted-foreground text-sm">{caption}</p>}
    </BlockSection>
)

/**
 * The first image carries the block; the rest are thumbnails under it. Any of them opens the
 * lightbox at its own position, which is why the click handler passes an index rather than a flag.
 */
export const GalleryRenderer = ({ title, images }: GalleryBlockData) => {
    const ordered = byOrder(images).filter((image) => Boolean(image.media?.url))
    const [openIndex, setOpenIndex] = useState<number | null>(null)
    const translations = useCmsTranslations()

    if (ordered.length === 0) return null

    const [hero, ...thumbnails] = ordered

    return (
        <BlockSection className="flex flex-col gap-4">
            <BlockTitle>{title}</BlockTitle>

            {hero && (
                <button
                    type="button"
                    onClick={() => setOpenIndex(0)}
                    aria-label={translations.gallery.open}
                    className="group block w-full overflow-hidden rounded-lg"
                >
                    <CmsImage
                        media={hero.media}
                        sizes="(min-width: 1024px) 72rem, 100vw"
                        className="aspect-[16/9] w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                    />
                </button>
            )}

            {thumbnails.length > 0 && (
                <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                    {thumbnails.map((image, index) => (
                        <li key={image.id}>
                            <button
                                type="button"
                                onClick={() => setOpenIndex(index + 1)}
                                aria-label={translations.gallery.imagePosition(
                                    index + 2,
                                    ordered.length,
                                )}
                                className="group block w-full overflow-hidden rounded-lg"
                            >
                                <CmsImage
                                    media={image.media}
                                    sizes="(min-width: 1024px) 18rem, 45vw"
                                    className="aspect-square w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <CmsLightbox
                images={ordered.map((image) => ({
                    id: image.id,
                    media: image.media,
                    caption: image.caption,
                }))}
                openIndex={openIndex}
                onClose={() => setOpenIndex(null)}
                title={title || undefined}
            />
        </BlockSection>
    )
}

export const SpeakerRenderer = ({ photoMedia, name, role }: SpeakerBlockData) => {
    if (!name && !role && !photoMedia?.url) return null

    return (
        <BlockSection className="!py-6">
            <div className="bg-muted/40 flex items-center gap-4 rounded-xl border p-4 sm:gap-6 sm:p-6">
                <CmsImage
                    media={photoMedia}
                    sizes="96px"
                    alt={name || undefined}
                    className="size-20 shrink-0 rounded-full object-cover sm:size-24"
                />
                <div className="flex flex-col gap-1">
                    {name && <span className="text-lg font-semibold lg:text-xl">{name}</span>}
                    {role && (
                        <span className="text-muted-foreground text-sm lg:text-base">{role}</span>
                    )}
                </div>
            </div>
        </BlockSection>
    )
}

export const FullWidthVideoRenderer = ({
    youtubeUrl,
    width,
    imageMedia,
    imageMediaMobile,
}: FullWidthVideoBlockData) => {
    const embedUrl = youtubeUrl ? buildYoutubeEmbedUrl(youtubeUrl) : undefined
    const [isPlaying, setIsPlaying] = useState(false)
    const { translations } = useRendererContext()

    const isContained = width === VideoWidth.CONTAINED

    /*
     * The frame is the same either way; only what it sits in changes. Rounding the corners just
     * for the contained variant is the one visual difference — a full-bleed element that is
     * rounded reads as a mistake against the edge of the viewport.
     */
    const frame = (
        <div
            className={cn(
                'relative aspect-video w-full overflow-hidden bg-black',
                isContained && 'rounded-xl',
            )}
        >
            {isPlaying ? (
                <iframe
                    // `autoplay` because the visitor already clicked play; without it they would
                    // have to click a second time inside the embed.
                    src={`${embedUrl}?autoplay=1`}
                    title={translations.video.play}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    className="absolute inset-0 size-full border-0"
                />
            ) : (
                /*
                 * The poster stands in for the player until someone asks for it. That is what the
                 * uploaded image is for, and it keeps youtube's player — and the requests that
                 * come with it — off every page view of a page that merely contains a video.
                 */
                <button
                    type="button"
                    onClick={() => setIsPlaying(true)}
                    aria-label={translations.video.play}
                    className="group absolute inset-0 size-full cursor-pointer"
                >
                    <CmsPicture
                        desktop={imageMedia}
                        mobile={imageMediaMobile}
                        sizes={isContained ? '(min-width: 1024px) 72rem, 100vw' : '100vw'}
                        className="size-full object-cover"
                    />
                    <span className="absolute inset-0 bg-black/20 transition-colors group-hover:bg-black/30" />
                    <span className="absolute left-1/2 top-1/2 flex size-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-lg transition-transform group-hover:scale-110 lg:size-20">
                        <Play className="ml-1 size-7 fill-current text-neutral-900 lg:size-9" />
                    </span>
                </button>
            )}
        </div>
    )

    // An unset or unrecognised url falls back to the poster rather than an empty black band.
    if (!embedUrl) {
        const poster = (
            <CmsPicture
                desktop={imageMedia}
                mobile={imageMediaMobile}
                sizes={isContained ? '(min-width: 1024px) 72rem, 100vw' : '100vw'}
                className={cn('h-auto w-full object-cover', isContained && 'rounded-xl')}
            />
        )
        return isContained ? (
            <BlockSection>{poster}</BlockSection>
        ) : (
            <section className="w-full">{poster}</section>
        )
    }

    return isContained ? (
        <BlockSection>{frame}</BlockSection>
    ) : (
        <section className="w-full">{frame}</section>
    )
}
